/**
 * useSync — desktop port of BigxBookApp/src/sync/useSync.ts.
 *
 * Same behaviour: trigger sync on foreground (window focus on desktop)
 * and on network restoration. RN imports replaced with platform shims
 * (`appState`, `netInfo`) defined in @/platform.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { appState, netInfo } from '@/platform';
import { useAppStore } from '../store/useAppStore';
import { syncDatabase } from './index';
import { useRealtimeSync } from './useRealtimeSync';
import { describeSyncError } from '@/utils/errorMessage';
import {
  reconcileInterruptedSync,
  markCycleStart,
  markCycleSuccess,
  markCycleFailure,
  isAutoSyncThrottled,
  resetCircuitBreaker,
} from './syncResumability';

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 2000;

let isCurrentlySyncing = false;

export function useSync() {
  const {
    isSyncing,
    setIsSyncing,
    lastSyncAt,
    setLastSyncAt,
    syncError,
    setSyncError,
    authToken,
    activeStoreId,
  } = useAppStore(
    useShallow((s) => ({
      isSyncing: s.isSyncing,
      setIsSyncing: s.setIsSyncing,
      lastSyncAt: s.lastSyncAt,
      setLastSyncAt: s.setLastSyncAt,
      syncError: s.syncError,
      setSyncError: s.setSyncError,
      authToken: s.authToken,
      activeStoreId: s.activeStoreId,
    })),
  );

  const lastWindowState = useRef<'active' | 'background'>(appState.current());
  const syncTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerSync = useCallback(
    async (isRetry = false, attempt = 1, isManual = false) => {
      if (isCurrentlySyncing && !isRetry) {
        // WEB ADAPTATION: tell the operator why the button did nothing.
        if (isManual) setSyncError('A sync is already running.');
        return;
      }
      if (!authToken || !activeStoreId) {
        // WEB ADAPTATION: this used to return silently, so a pressed "Sync now"
        // looked dead while the footer kept showing the last successful time.
        // On web that state is reachable — the access token is memory-only, so
        // it is null after a reload until it has been re-minted.
        if (isManual) {
          setSyncError(
            !activeStoreId
              ? 'No store selected — pick a store before syncing.'
              : 'Session expired. Sign in again to sync.',
          );
        }
        return;
      }

      // Circuit breaker: throttle auto-triggers when sync has been failing
      // repeatedly. Manual triggers from the UI bypass and reset the breaker.
      if (!isRetry && !isManual) {
        const throttledUntil = await isAutoSyncThrottled();
        if (throttledUntil) {
          console.warn(
            `[useSync] Auto-sync throttled until ${new Date(throttledUntil).toLocaleTimeString()} ` +
            `— use "Sync now" to force.`
          );
          return;
        }
      } else if (isManual) {
        // Manual trigger — reset the breaker so we always attempt.
        await resetCircuitBreaker();
      }

      isCurrentlySyncing = true;

      const online = await netInfo.isOnline();
      if (!online) {
        isCurrentlySyncing = false;
        setSyncError('No internet connection');
        return;
      }

      setIsSyncing(true);
      setSyncError(null);

      // Resumability bookkeeping: only mark a NEW cycle (not a retry).
      if (!isRetry) await markCycleStart();

      try {
        console.log(`[useSync] Starting syncDatabase (Attempt ${attempt})...`);
        const didSync = await syncDatabase();
        if (didSync) {
          console.log('[useSync] Sync completed successfully.');
          setLastSyncAt(Date.now());
          await markCycleSuccess();
        } else {
          console.log('[useSync] Sync skipped (not authenticated or already in progress).');
          // Skipped — don't record success or failure; clear the in-progress flag.
          await markCycleSuccess();
        }
        isCurrentlySyncing = false;
        setIsSyncing(false);
      } catch (error: unknown) {
        const err = error as Error;
        console.error(`[useSync] Sync attempt ${attempt} failed:`, err);
        isCurrentlySyncing = false;
        setIsSyncing(false);

        // Don't retry rate-limit errors — retrying just deepens the throttle hole.
        // Surface to the UI and let the user/circuit-breaker decide when to try again.
        const isRateLimited = /\b429\b|too many requests|throttler/i.test(err?.message ?? '');

        if (!isRateLimited && attempt < MAX_RETRIES) {
          const delay = BASE_DELAY_MS * Math.pow(2, attempt - 1);
          console.log(`[useSync] Retrying sync in ${delay}ms...`);
          syncTimeout.current = setTimeout(() => {
            triggerSync(true, attempt + 1, isManual);
          }, delay);
        } else {
          // Whatever this was, the person reading it is standing at a till.
          // describeSyncError answers the question they actually have — can I
          // keep selling? — instead of naming the exception.
          const msg = describeSyncError(error);
          setSyncError(msg);
          await markCycleFailure(msg);
        }
      }
    },
    [authToken, activeStoreId, setIsSyncing, setSyncError, setLastSyncAt],
  );

  // Realtime "signal-to-pull": when a peer device changes this store, schedule a
  // debounced sync. Pure optimization on top of the focus/network triggers below.
  useRealtimeSync(useCallback(() => { void triggerSync(); }, [triggerSync]));

  // Reconcile any sync interrupted by a previous app crash. Runs once per
  // mount; the helper is idempotent so duplicate calls are harmless.
  useEffect(() => {
    void reconcileInterruptedSync();
  }, []);

  // 1. Auto-sync when window regains focus (desktop equivalent of app foreground).
  useEffect(() => {
    const unsubscribe = appState.subscribe((next) => {
      if (lastWindowState.current === 'background' && next === 'active') {
        console.log('Window focused, triggering sync...');
        triggerSync();
      }
      lastWindowState.current = next;
    });
    return unsubscribe;
  }, [triggerSync]);

  // 2. Auto-sync when network connection is restored.
  const wasConnected = useRef<boolean | null>(null);
  useEffect(() => {
    const unsubscribe = netInfo.subscribe((isNowConnected) => {
      if (isNowConnected && wasConnected.current === false) {
        console.log('[useSync] Network restored, triggering sync...');
        triggerSync();
      }
      wasConnected.current = isNowConnected;
    });
    return () => {
      unsubscribe();
      if (syncTimeout.current) clearTimeout(syncTimeout.current);
    };
  }, [triggerSync]);

  return {
    isSyncing,
    lastSyncAt,
    syncError,
    triggerSync: () => triggerSync(false, 1, /* isManual */ true),
  };
}
